import React from "react";
import {
  Menu,
  Badge,
  Text,
  Group,
  Stack,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import {
  IconClock,
  IconCheck,
  IconBan,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";
import type { OCRExtraction } from "../../types";
import { useProfileStore } from "../../store/profileStore";

interface OCRExtractionSelectorProps {
  /**
   * The document ID
   */
  docId: string;
  /**
   * List of OCR extractions for this document
   */
  extractions: OCRExtraction[];
  /**
   * Callback when an extraction is selected
   */
  onSelect: (extractionId: number) => void;
}

/**
 * Component that displays a menu to select from multiple OCR extractions
 * for a single document.
 */
export const OCRExtractionSelector: React.FC<OCRExtractionSelectorProps> = ({
  docId,
  extractions,
  onSelect,
}) => {
  const { selectedExtractionIds, toggleExtractionEnabled } = useProfileStore();
  const selectedId = selectedExtractionIds[docId];

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format user type for display
  const formatUserType = (userType: string): string => {
    const userTypeMap: Record<string, string> = {
      taxClient: "Tax Client",
      spouse: "Spouse",
      dependent: "Dependent",
    };
    return userTypeMap[userType] || userType;
  };

  const handleToggle = (e: React.MouseEvent, extraction: OCRExtraction) => {
    e.stopPropagation();
    toggleExtractionEnabled(extraction.id, !extraction.enabled);
  };

  // Sort extractions by creation date (newest first)
  const sortedExtractions = [...extractions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Menu.Dropdown>
      <Menu.Label>Select OCR Extraction</Menu.Label>
      <Stack gap="xs">
        {sortedExtractions.map((extraction, index) => {
          const isSelected = selectedId === extraction.id;
          const isLatest = index === 0;
          const isDisabled = extraction.enabled === false;

          const menuItem = (
            <Menu.Item
              key={extraction.id}
              onClick={() => onSelect(extraction.id)}
              leftSection={isSelected ? <IconCheck size={16} /> : null}
              style={{
                backgroundColor: isSelected
                  ? "rgba(34, 139, 230, 0.1)"
                  : undefined,
                opacity: isDisabled ? 0.6 : 1,
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2}>
                  <Group gap="xs">
                    <Text
                      size="sm"
                      fw={500}
                      style={{
                        textDecoration: isDisabled ? "line-through" : undefined,
                      }}
                    >
                      {formatUserType(extraction.userType)} - {extraction.form}
                    </Text>
                    {isLatest && (
                      <Badge size="xs" color="blue" variant="light">
                        Latest
                      </Badge>
                    )}
                    {isDisabled && (
                      <Badge
                        size="xs"
                        color="red"
                        variant="light"
                        leftSection={<IconBan size={10} />}
                      >
                        Disabled
                      </Badge>
                    )}
                  </Group>
                  <Group gap="xs" justify="space-between">
                    <Group gap="xs">
                      <IconClock size={12} />
                      <Text size="xs" c="dimmed">
                        {formatDate(extraction.createdAt)}
                      </Text>
                    </Group>
                    <Tooltip
                      label={
                        isDisabled ? "Enable extraction" : "Disable extraction"
                      }
                    >
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color={isDisabled ? "green" : "red"}
                        onClick={(e) => handleToggle(e, extraction)}
                      >
                        {isDisabled ? (
                          <IconEye size={14} />
                        ) : (
                          <IconEyeOff size={14} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  {extraction.ocrReview?.ocr_quality != null && (
                    <Text size="xs" c="dimmed">
                      Quality: {String(extraction.ocrReview.ocr_quality)}
                    </Text>
                  )}
                </Stack>
              </Group>
            </Menu.Item>
          );

          if (isDisabled) {
            return (
              <Tooltip
                key={extraction.id}
                label="This extraction is disabled and will not be used for form filling"
                multiline
                w={220}
              >
                {menuItem}
              </Tooltip>
            );
          }

          return menuItem;
        })}
      </Stack>
    </Menu.Dropdown>
  );
};
